// ── Google Analytics 4 helper ─────────────────────────────────────────────────
// All functions are no-ops if gtag isn't loaded (e.g. dev without a GA ID).

const gtag = (...args) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
};

// ── Page tracking ─────────────────────────────────────────────────────────────
export const trackPageView = (path, title) => {
  gtag('event', 'page_view', {
    page_path:     path,
    page_title:    title || document.title,
    page_location: window.location.href,
  });
};

// ── Auth events ───────────────────────────────────────────────────────────────
export const trackSignUp = (method = 'email') => {
  gtag('event', 'sign_up', { method });
};

export const trackLogin = (method = 'email') => {
  gtag('event', 'login', { method });
};

// ── Story events ──────────────────────────────────────────────────────────────
export const trackStoryGenerated = ({ childAge, language, isMultiChild } = {}) => {
  gtag('event', 'generate_story', {
    child_age:      childAge,
    language:       language || 'en',
    is_multi_child: isMultiChild || false,
  });
};

export const trackStoryRead = (storyId) => {
  gtag('event', 'read_story', { story_id: storyId });
};

export const trackStoryShared = (method) => {
  gtag('event', 'share', {
    method,
    content_type: 'story',
  });
};

export const trackStoryFavorited = () => {
  gtag('event', 'favorite_story');
};

// ── Subscription / revenue events ─────────────────────────────────────────────
export const trackBeginCheckout = (plan, billing, value) => {
  gtag('event', 'begin_checkout', {
    currency: 'USD',
    value,
    items: [{ item_id: `${plan}_${billing}`, item_name: `${plan} (${billing})` }],
  });
};

export const trackSubscriptionChanged = (plan) => {
  gtag('event', 'subscription_changed', { plan });
};

// ── Blog events ───────────────────────────────────────────────────────────────
export const trackBlogView = (slug, title) => {
  gtag('event', 'blog_view', { blog_slug: slug, blog_title: title });
};

export const trackBlogCtaClick = (slug) => {
  gtag('event', 'blog_cta_click', {
    blog_slug:   slug,
    cta_label:   'Start Free',
    destination: '/register',
  });
};

// ── Engagement events ─────────────────────────────────────────────────────────
export const trackSearch = (query, section) => {
  gtag('event', 'search', { search_term: query, section });
};

export const trackContactFormSubmit = (subject) => {
  gtag('event', 'contact_form_submit', { subject });
};

export const trackGiftPurchase = (type) => {
  gtag('event', 'gift_purchase', { gift_type: type });
};

export const trackPricingView = (billingToggle) => {
  gtag('event', 'view_item_list', {
    item_list_name: 'Pricing Plans',
    billing:        billingToggle,
  });
};

export const trackVoiceCloneCreated = () => {
  gtag('event', 'voice_clone_created');
};

export const trackQuizAttempted = (storyId, score) => {
  gtag('event', 'quiz_attempted', { story_id: storyId, score });
};

export const trackCommunityPublish = () => {
  gtag('event', 'community_publish');
};

export const trackCommunityLike = () => {
  gtag('event', 'community_like');
};

export const trackNarrationPlayed = (type) => {
  gtag('event', 'narration_played', { narration_type: type }); // 'ai' | 'clone' | 'browser'
};

export const trackGiftRedeemed = (plan) => {
  gtag('event', 'gift_redeemed', { plan });
};

export const trackProfileUpdated = () => {
  gtag('event', 'profile_updated');
};

export const trackPdfExported = () => {
  gtag('event', 'pdf_exported');
};

export const trackQuizSubmitted = (storyId, score, total) => {
  gtag('event', 'quiz_submitted', {
    story_id:  storyId,
    score,
    total,
    pct: total > 0 ? Math.round((score / total) * 100) : 0,
  });
};

export const trackIllustrationDownloaded = () => {
  gtag('event', 'illustration_downloaded');
};

// ── Child profile events ───────────────────────────────────────────────────────
export const trackChildCreated = (age) => {
  gtag('event', 'child_created', { child_age: age });
};

export const trackChildDeleted = () => {
  gtag('event', 'child_deleted');
};

export const trackChildPhotoUploaded = () => {
  gtag('event', 'child_photo_uploaded');
};

export const trackChildPhotoDeleted = () => {
  gtag('event', 'child_photo_deleted');
};

// ── Story approval events ─────────────────────────────────────────────────────
export const trackStoryApproved = () => {
  gtag('event', 'story_approved');
};

export const trackStoryRejected = () => {
  gtag('event', 'story_rejected');
};

// ── Narration / read-aloud events ─────────────────────────────────────────────
export const trackReadAloud = (language) => {
  gtag('event', 'read_aloud', { language: language || 'en' });
};

// ── Community events ──────────────────────────────────────────────────────────
export const trackCommunityView = (slug) => {
  gtag('event', 'community_story_view', { story_slug: slug });
};

export const trackCommunityStoryReadAloud = (language) => {
  gtag('event', 'community_read_aloud', { language: language || 'en' });
};

export const trackSharedStoryView = () => {
  gtag('event', 'shared_story_view');
};

export const trackSharedStoryReaction = (emoji) => {
  gtag('event', 'shared_story_reaction', { emoji });
};

export const trackSharedStoryReadAloud = (language) => {
  gtag('event', 'shared_read_aloud', { language: language || 'en' });
};

// ── Referral / gift events ────────────────────────────────────────────────────
export const trackReferralClaim = () => {
  gtag('event', 'referral_claim');
};

export const trackReferralReadAloud = (language) => {
  gtag('event', 'referral_read_aloud', { language: language || 'en' });
};

export const trackGiftSuccess = (plan) => {
  gtag('event', 'gift_purchase_success', { plan });
};

// ── Series events ─────────────────────────────────────────────────────────────
export const trackSeriesChapterGenerated = (chapterNumber, isFinal) => {
  gtag('event', 'series_chapter_generated', { chapter_number: chapterNumber, is_final: isFinal });
};

export const trackSeriesDeleted = () => {
  gtag('event', 'series_deleted');
};

export const trackSeriesCreated = () => {
  gtag('event', 'series_created');
};

// ── Landing / marketing events ────────────────────────────────────────────────
export const trackLandingCta = (label, destination) => {
  gtag('event', 'landing_cta_click', { cta_label: label, destination });
};

// A website visitor tapped an App Store / Google Play badge.
// store: 'app_store' | 'google_play'; placement: where the badge sits.
export const trackAppStoreClick = (store, placement) => {
  gtag('event', 'app_store_click', { store, placement });
};

export const trackLandingDemoPlay = () => {
  gtag('event', 'landing_demo_play');
};

export const trackPreviewGenerated = (age) => {
  gtag('event', 'landing_preview_generated', { child_age: age });
};

export const trackPreviewEmailCaptured = (age) => {
  gtag('event', 'landing_preview_email_captured', { child_age: age });
};

export const trackPreviewSignupClick = () => {
  gtag('event', 'landing_preview_signup_click');
};

export const trackStoryOfDayReadClick = (slug) => {
  gtag('event', 'story_of_day_read_click', { slug });
};

export const trackLeadCaptured = (source) => {
  gtag('event', 'lead_captured', { source });
};

export const trackStreakMilestone = (nights) => {
  gtag('event', 'streak_milestone', { nights });
};

export const trackAchievementUnlocked = (badgeId) => {
  gtag('event', 'achievement_unlocked', { badge_id: badgeId });
};

export const trackStoryOfDayListen = (slug) => {
  gtag('event', 'story_of_day_listen', { slug });
};

// ── Dashboard events ──────────────────────────────────────────────────────────
export const trackTopUpOpened = (packType) => {
  gtag('event', 'topup_opened', { pack_type: packType });
};

export const trackTopUpCompleted = (packType) => {
  gtag('event', 'topup_completed', { pack_type: packType });
};

// ── Illustration / story reader extra events ──────────────────────────────────
export const trackIllustrationRegenerated = () => {
  gtag('event', 'illustration_regenerated');
};

export const trackStoryDeleted = () => {
  gtag('event', 'story_deleted');
};

export const trackStoryUnpublished = () => {
  gtag('event', 'story_unpublished');
};

// ── Blog listing events ───────────────────────────────────────────────────────
export const trackBlogSearch = (query) => {
  gtag('event', 'blog_search', { search_term: query });
};

export const trackBlogTagFilter = (tag) => {
  gtag('event', 'blog_tag_filter', { tag });
};

// ── Checkout / purchase events ────────────────────────────────────────────────
export const trackPurchase = (plan, billing, value) => {
  gtag('event', 'purchase', {
    currency: 'USD',
    value,
    items: [{ item_id: `${plan}_${billing}`, item_name: `${plan} (${billing})` }],
  });
};

export const trackCheckoutSuccess = (plan, billing) => {
  gtag('event', 'checkout_success', { plan, billing });
};

// ── Library events ────────────────────────────────────────────────────────────
export const trackLibraryFiltered = (filterType, value) => {
  gtag('event', 'library_filtered', { filter_type: filterType, filter_value: value });
};

export const trackLibraryStoryOpened = (storyId) => {
  gtag('event', 'library_story_opened', { story_id: storyId });
};

// ── Settings events ───────────────────────────────────────────────────────────
export const trackVoiceCloneStarted = () => {
  gtag('event', 'voice_clone_started');
};

export const trackReminderSaved = (enabled) => {
  gtag('event', 'reminder_saved', { enabled });
};

export const trackPushNotificationEnabled = () => {
  gtag('event', 'push_notification_enabled');
};

export const trackBillingPortalOpened = () => {
  gtag('event', 'billing_portal_opened');
};

export const trackLogout = () => {
  gtag('event', 'logout');
};

// ── Pricing events ────────────────────────────────────────────────────────────
export const trackPricingBillingToggled = (billing) => {
  gtag('event', 'pricing_billing_toggled', { billing });
};

export const trackPricingPlanViewed = (plan) => {
  gtag('event', 'pricing_plan_viewed', { plan });
};

// ── Report events ─────────────────────────────────────────────────────────────
export const trackReadingReportViewed = (childId) => {
  gtag('event', 'reading_report_viewed', { child_id: childId });
};

// ── Affiliate events ──────────────────────────────────────────────────────────
export const trackAffiliatePageViewed = () => {
  gtag('event', 'affiliate_page_viewed');
};

export const trackAffiliateApplyStarted = () => {
  gtag('event', 'affiliate_apply_started');
};

export const trackAffiliateApplySubmitted = (country) => {
  gtag('event', 'affiliate_apply_submitted', { country });
};

export const trackAffiliateLinkCopied = () => {
  gtag('event', 'affiliate_link_copied');
};

export const trackAffiliateCodeCopied = () => {
  gtag('event', 'affiliate_code_copied');
};

export const trackAffiliateConnectStarted = () => {
  gtag('event', 'affiliate_connect_started');
};

export const trackAffiliateConnectCompleted = () => {
  gtag('event', 'affiliate_connect_completed');
};

export const trackAffiliateStripeDashboardOpened = () => {
  gtag('event', 'affiliate_stripe_dashboard_opened');
};

export const trackAffiliateProfileSaved = () => {
  gtag('event', 'affiliate_profile_saved');
};

export const trackAffiliateLinkClicked = (code) => {
  gtag('event', 'affiliate_link_clicked', { affiliate_code: code });
};

// ── Series events ─────────────────────────────────────────────────────────────
export const trackSeriesViewed = (seriesId) => {
  gtag('event', 'series_viewed', { series_id: seriesId });
};

// ── Dashboard events ──────────────────────────────────────────────────────────
export const trackDashboardGenerateCta = () => {
  gtag('event', 'dashboard_generate_cta');
};

// ── Onboarding events ─────────────────────────────────────────────────────────
export const trackOnboardingStepView = (step) => {
  gtag('event', 'onboarding_step_view', { step });
};

export const trackOnboardingStepComplete = (step) => {
  gtag('event', 'onboarding_step_complete', { step });
};

export const trackOnboardingSkipped = (step) => {
  gtag('event', 'onboarding_skipped', { step });
};

export const trackOnboardingCompleted = ({ tone, interests_count, reminder_enabled } = {}) => {
  gtag('event', 'onboarding_completed', {
    tone,
    interests_count,
    reminder_enabled,
  });
};
